import axiosConfig from "./axiosConfig";

// Map-viewport bounding box for a "Search this area" search. Matches the
// shape a Leaflet `map.getBounds()` serialises to; the BE maps it to the
// postcode-district (outcode) set it covers and searches those. Sent
// INSTEAD of `postcodeOutward` - a typed postcode always wins if both are
// somehow present.
export interface LeadSearchViewportBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface LeadSearchRequest {
  postcodeOutward?: string;
  minFloorAreaSqm?: number;
  maxFloorAreaSqm?: number;
  radiusKm?: number;
  // Present for a "Search this area" (map-viewport) search. The current
  // floor-area filters still apply; the area comes from the map instead of
  // a typed postcode.
  viewportBounds?: LeadSearchViewportBounds;
  // Bypass the per-row enrichment cache. Clears `*EnrichedAt` on every
  // matching VOA row + re-runs every external lookup (geocode +
  // buildings + HMLR + CH). Always returns `status: "pending"` because
  // a clear is by definition a cold-cache search.
  forceRefetch?: boolean;
  // Two-step UX. First call leaves this unset and the backend returns
  // `status: "preview"` with row counts so the FE can show a confirm
  // modal. Second call passes `confirmEnrichment: true` to actually
  // clear cache + spawn the worker. Searches that hit the all-cached
  // fast path skip the prompt entirely (no external API cost).
  confirmEnrichment?: boolean;
}

export interface HmlrMatch {
  proprietorName: string;
  proprietorAddress: string | null;
  tenure: string | null;
  titleNumber: string | null;
  companyRegistrationNumber: string | null;
  distanceMetres: number | null;
  confidence: number;
  // Raw GeoJSON polygon coords for the title boundary. Optional for
  // backwards compatibility with rows enriched before the field was
  // added. The right panel uses this to filter the HMLR list to deeds
  // overlapping the operator's currently selected building.
  polygon?: number[][][] | number[][][][] | null;
  // Cached server-side bbox: [west, south, east, north].
  bbox?: [number, number, number, number] | null;
  // When the proprietor name fuzzy-matches an existing platform
  // Organisation, the BE attaches `orgId` + `orgName` so the FE can
  // render the cleaner platform name in place of the raw HMLR
  // proprietor (e.g. "B&Q" not "B & Q PROPERTIES LIMITED").
  orgId?: string | null;
  orgName?: string | null;
}

export interface EpcMatch {
  // 20-digit certificate number from the MHCLG non-domestic register.
  // Stable across the legacy + new APIs; replaced the legacy LMK_KEY
  // field name when the Get Energy Performance of Buildings service
  // launched (2026-05).
  certificateNumber: string;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  postcode: string | null;
  totalFloorAreaSqm: number | null;
  // SBEM numeric energy rating (lower is better, 0-150 typical range).
  assetRating: number | null;
  // A-G band derived from `assetRating` (A is best).
  assetRatingBand: string | null;
  propertyType: string | null;
  transactionType: string | null;
  inspectionDate: string | null;
  lodgementDate: string | null;
  localAuthorityLabel: string | null;
  buildingReferenceNumber: string | null;
}

export interface TenantMatch {
  companyName: string;
  // Optional - Companies House matches always have one (the canonical
  // identifier the platform stores on SiteOwnership). Google Places
  // matches don't (Places returns business names, not CH identifiers);
  // the operator can cross-reference manually if they want to.
  companyNumber: string | null;
  companyStatus: string | null;
  sicCodes: string[];
  registeredAddress: string | null;
  confidence: number;
  // Where the match came from. "ch" = Companies House search-by-
  // postcode (broad recall, lots of dormant orgs); "google_places" =
  // Google Places Nearby Search at the building's lat/lon (address-
  // precise, returns the actual operating business). Optional for
  // backwards compat with rows enriched before the Places source was
  // added.
  source?: "ch" | "google_places" | "osm";
  googlePlaceId?: string | null;
  // OSM element id ("node/123" / "way/456") when source=osm. Lets the
  // operator follow the link back to OSM if they want to corroborate.
  osmId?: string | null;
  // The company's website when the source provides one (the OSM
  // `website` tag today). Threaded onto the tenant decision so
  // people-discovery can hand Apollo a precise domain.
  website?: string | null;
  // Distance from the building anchor in metres - populated for
  // Places + OSM matches.
  distanceMetres?: number | null;
  // Business lat/lon. Populated for Places + OSM matches (the FE
  // uses these to render a hover-preview marker on the map). Null
  // for CH (postcode-keyed, no per-company coordinate).
  latitude?: number | null;
  longitude?: number | null;
  // The raw Places `displayName` before our brand-stemming cleanup
  // ran (e.g. "Costco Bristol" before stemming to "Costco"). Only
  // present when stemming changed the name; the FE renders it in the
  // row detail so operators can spot bad stems and report them.
  originalCompanyName?: string | null;
  // When the company name fuzzy-matches an existing platform
  // Organisation, the BE attaches `orgId` + `orgName` so the FE can
  // render the cleaner platform name in place of the raw match
  // value (e.g. "Costco Wholesale" not "Costco" from OSM tags). Same
  // pattern as HmlrMatch.
  orgId?: string | null;
  orgName?: string | null;
}

export interface AgenticCitation {
  url: string;
  title: string;
  publishedDate: string;
  quote: string;
  // R12 evidence tier of this source: "A" registry-grade / "B" owner
  // self-declaration / "C" deal reporting / "D" weak. "" or absent on older runs.
  tier?: string;
}

// R16 ownership chain for one candidate (any field may be blank/absent).
export interface AgenticChainRoles {
  brand?: string;
  manager?: string;
  fund?: string;
  likelyProprietor?: string;
}

// R26: one adjudication line per primed (registry) candidate.
export interface AgenticAdjudication {
  name: string;
  // CONFIRMED / CONTRADICTED / NO_EVIDENCE / NOT_PURSUED, else "".
  status: string;
  note?: string;
}

// R17/R24: a named party that is NOT the landlord, with its role + why-not.
export interface AgenticRelatedParty {
  name: string;
  role: string;
  note?: string;
}

// The R24 decision block + R26 lists of a landlord-research run. Every field is
// optional - an older-shape answer (candidates only) has no decision block.
export interface AgenticDecision {
  // PROCEED / PROCEED_WITH_CAUTION / WITHHELD_AT_SOURCE / INSUFFICIENT /
  // NOT_ESTABLISHED, else "".
  verdict?: string;
  decisionLine?: string;
  partyToContact?: string;
  nextAction?: string;
  adjudications?: AgenticAdjudication[];
  relatedParties?: AgenticRelatedParty[];
  unreachable?: string[];
  notes?: string;
}

// One agentic landlord-research candidate (design PR #1265). Produced by the
// research agent, not an enrichment source; carries its supporting citations.
// The right-panel default-pick + left-table top-landlord skip agentic rows (they
// read the HMLR pool), but the BE DOES auto-select the top candidate into the
// persisted landlord when its confidence is over 60% - see
// `_auto_pick_landlord_selection`; that path is confidence-keyed, not gated on
// `eligibleForAutoPick`.
export interface AgenticMatch {
  source: "agentic";
  taskKey: string;
  proprietorName: string;
  companyNumber: string | null;
  confidence: number;
  reasoning: string;
  citations: AgenticCitation[];
  // Always false - agentic candidates carry no deed polygon so they never enter
  // the HMLR default-pick filter this flag gates. (Research auto-selection is a
  // separate, confidence-keyed path and does not read this flag.)
  eligibleForAutoPick: boolean;
  promptRevision: number;
  researchedAt?: string | null;
  // Attached by the BE when the proprietor fuzzy-matches a platform Org.
  orgId?: string | null;
  orgName?: string | null;
  // R18 match granularity ("unit"/"estate"/"sole-building"/"estate-partial"/
  // "street"/"town", "" if unstated), R26 not-on-primed-list flag, and R16
  // ownership-chain roles. Absent on older runs.
  matchGranularity?: string;
  notOnList?: boolean;
  chainRoles?: AgenticChainRoles;
}

export interface LeadCandidate {
  source: string;
  sourceRef: string;
  address: string | null;
  postcode: string | null;
  postcodeOutward: string | null;
  latitude: number | null;
  longitude: number | null;
  // Which geocoder produced (latitude, longitude). 'mapbox' (HIGH /
  // MEDIUM) is address-precise; 'postcodes_io' (POSTCODE_CENTROID) is
  // the postcode centroid fallback.
  geocodeSource: string | null;
  geocodeConfidence: string | null;
  // Full per-source attempt list (mapbox, google_address_validation,
  // google_places, postcodes_io). One of them has wasPicked=true. Used
  // by the right panel to show how much agreement there was between
  // sources, so the operator can weigh the building-distance metric.
  geocodeAttempts: GeocodeAttempt[] | null;
  floorAreaSqm: number | null;
  rateableValue: number | null;
  primaryDescription: string | null;
  scatDescription: string | null;
  hmlrMatches: HmlrMatch[];
  tenantMatches: TenantMatch[];
  // EPC non-domestic register hits at this row's postcode (most-recent
  // inspection first). Used by the row detail panel for floor-area
  // corroboration + energy-rating display. Each entry's shape is
  // documented in domain/services/enrichment/epc.py on the BE side.
  epcMatches: EpcMatch[];
  // Agentic landlord-research candidates. Empty until an operator runs
  // "Research landlord" on the row; rendered as a distinct source in the
  // landlord options table, never auto-selected. See AgenticMatch.
  agenticMatches: AgenticMatch[];
  // AI-trace run_id of the most recent landlord-research run on this row, so the
  // right panel opens that run's trace from the row alone (null until a run has
  // completed). The lead inbox carries the equivalent in `agenticData.traceRunId`.
  agenticTraceRunId: string | null;
  // The R24 decision block + R26 lists of the most recent research run (verdict,
  // decision line, adjudications, related parties, unreachable). null until R24/R26
  // land a decision; the lead inbox carries the equivalent in `agenticData.decision`.
  agenticDecision?: AgenticDecision | null;
  // Building footprint resolved from datasets-api around the postcode-centroid.
  // When present, the map pin should sit on (buildingLatitude, buildingLongitude)
  // and the polygon outline should render. buildingPolygon coords are GeoJSON
  // [[[lon, lat], ...]] (Polygon) or [[[[lon, lat], ...]]] (MultiPolygon).
  buildingPolygon: number[][][] | number[][][][] | null;
  buildingLatitude: number | null;
  buildingLongitude: number | null;
  buildingSurfaceAreaSqm: number | null;
  buildingDistanceMetres: number | null;
  // Top-N polygon candidates ranked best-first with score breakdown.
  // `buildingCandidates[0]` mirrors the chosen building above; runners-up
  // let the operator validate why polygon #1 was picked.
  buildingCandidates: BuildingCandidate[] | null;
  matchConfidence: number | null;
  existingLeadId: string | null;
  // Set when the resolved building polygon overlaps a site already on
  // the platform. Operator should NOT onboard this lead unless the
  // platform site is being re-platformed. Capitalised `ID` to match
  // the rest of the codebase's ID suffix convention.
  matchedExistingSiteID: string | null;
  matchedExistingSiteName: string | null;
  // VOA-side review state. Edits on /leads/search write these onto
  // the VOA row; promotion to a Lead happens explicitly per
  // sourceRef. UI status derives from these:
  //   existingLeadId -> 'Already a lead'
  //   rejectedAt     -> 'Rejected'
  //   reviewedAt     -> 'Reviewed'
  //   else           -> 'New'
  selectedBuildingBkey: string | null;
  selectedLandlord: SelectedOrg | null;
  selectedTenant: SelectedOrg | null;
  ownerOccupier: boolean;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  // When each enrichment pass last hit upstream for this row.
  // Surfaced on the right panel so the operator can see the data
  // freshness per source.
  geocodedAt: string | null;
  buildingResolvedAt: string | null;
  hmlrEnrichedAt: string | null;
  tenantEnrichedAt: string | null;
  // Set whenever the EPC pass has run for this row (whether or not it
  // returned any certs at the postcode).
  epcEnrichedAt: string | null;
  // Set when the agentic landlord-research agent has been run on this row.
  // Null = never researched; drives the "Research landlord" button's
  // researched/not-researched state.
  agenticResearchedAt: string | null;
  // Set when the paid Google Places passes have been run on this row
  // (operator-opt-in via /leads/voa-premium-enrich). Null = default
  // cheap-path data only; the per-row "Enrich with paid APIs" button
  // hides itself once this is set.
  premiumEnrichedAt: string | null;
  // Sources for which the most recent enrichment attempt was
  // prevented (circuit breaker open, transport error, 5xx, 429).
  // Empty list = clean run; missing/null = unknown (row was last
  // enriched before this column landed). Possible values:
  // "overpass", "companies_house". The FE surfaces this as a per-
  // row badge on /leads/search and offers a bulk refetch-degraded
  // action so the operator can re-run only the missing sources.
  enrichmentDegradedSources: string[];
}

export interface SelectedOrg {
  orgId?: string | null;
  displayName: string;
  // `google_places` was added when Places became a co-equal tenant
  // resolver; `osm` was added when OpenStreetMap Overpass was wired
  // in as a free Stage-1 source. The BE stores the selection as
  // JSONB and doesn't validate the enum, so adding a new value is
  // FE-only.
  source:
    | "hmlr"
    | "ch"
    | "google_places"
    | "osm"
    | "platform"
    | "manual"
    | "agentic";
  hmlrTitleNumber?: string | null;
  chCompanyNumber?: string | null;
  // Populated only when `source === "google_places"` so the
  // selected-row identity round-trips correctly (Places matches don't
  // have a CH company number, so without this the radio button on
  // the picked row never re-checks after click).
  googlePlaceId?: string | null;
  // Populated only when `source === "osm"`. Same role as
  // `googlePlaceId` for Places matches - lets the saved-row identity
  // survive a panel re-mount.
  osmId?: string | null;
  // The company's website, carried through only for `source === "osm"`
  // (the OSM `website` tag). Persisted on the decision so people-discovery
  // can hand Apollo a precise domain instead of a fuzzy trading-name search.
  website?: string | null;
}

export interface GeocodeAttempt {
  source: string;
  confidenceLabel: string | null;
  lat: number | null;
  lon: number | null;
  wasPicked: boolean;
}

export interface BuildingCandidate {
  // Stable key derived from centroid+area; lets the operator's pick
  // survive re-fetch and lines up with `selectedBuildingBkey` on the
  // VOA row (or `acceptedBkey` on a Lead's polygonDecision).
  bkey: string;
  polygon: number[][][] | number[][][][];
  centroidLat: number;
  centroidLon: number;
  surfaceAreaSqm: number | null;
  distanceMetres: number;
  // Per-candidate 0-100 confidence (distance-weighted blend with the
  // VOA-declared area, matching `LeadCandidate.matchConfidence`). This
  // is the field to render in the UI - `score` is the raw scorer
  // output and isn't on a fixed scale.
  confidence: number | null;
  // Raw scorer output: `area_weight * proximity * area_match`. Range
  // depends on building size (`area_weight = log10(surfaceAreaSqm)`)
  // so don't display this as a percentage; sort with it at most.
  score: number | null;
  proximityWeight: number | null;
  areaWeight: number | null;
  areaMatch: number | null;
  // True when `areaMatch` is below the BE's MIN_AREA_MATCH_WITH_DECLARED_AREA
  // (0.4). The resolver kept the polygon (otherwise the operator wouldn't
  // have anything to pick from) but it sorts after any above-floor peer
  // and its confidence is penalised. Optional for backwards-compat with
  // rows enriched before this flag was added.
  belowAreaMatchFloor?: boolean;
  // True when at least one geocoder anchor landed inside this polygon.
  // Used by the BE as a tier-2 promotion within the below-floor branch
  // ("the pin actually fell on this building" outranks larger-but-distant
  // peers). Optional for backwards-compat.
  pinOverlapsPolygon?: boolean;
  // Per-polygon platform-site overlap. Populated by the BE for every
  // building candidate so the right-panel warning can update as the
  // operator picks a different polygon. `LeadCandidate.matchedExistingSiteID`
  // mirrors the auto-resolved top pick (`buildingCandidates[0]`) for the
  // row-level "On platform" badge.
  matchedExistingSiteID?: string | null;
  matchedExistingSiteName?: string | null;
}

// Three response shapes:
// - "completed": every matched row is already cache-hot and no
//   refetch was requested. Candidates returned inline, no prompt.
// - "preview": the search would trigger external API calls. The FE
//   shows a confirm modal with the cost-relevant counts; on confirm
//   it re-issues the same request with `confirmEnrichment: true`.
// - "pending": worker has been started. The FE polls `/async_status`
//   until completed, then re-issues the search to get the completed
//   shape.
export type LeadSearchResponse =
  | LeadSearchCompletedResponse
  | LeadSearchPreviewResponse
  | LeadSearchPendingResponse;

export interface LeadSearchCompletedResponse {
  status: "completed";
  candidates: LeadCandidate[];
  totalMatched: number;
  totalReturned: number;
  truncated: boolean;
  // The expanded outcode set the SQL filter actually queried (seed
  // outcode + every neighbour within radiusKm). The map renders an
  // outline polygon per outcode so the operator can see the
  // geographic extent of "what we're searching", not just the seed.
  expandedOutcodes: string[];
  // Rows whose persisted enrichment failed Pydantic validation on
  // the BE-side per-row build. The BE Sentry-captures each one and
  // skips it; the FE surfaces the count so operators know to ask
  // engineering to investigate. Empty when every row materialised
  // cleanly.
  failedRowCount?: number;
  failedRowRefs?: string[];
}

export interface LeadSearchPreviewResponse {
  status: "preview";
  totalMatched: number;
  totalReturned: number;
  truncated: boolean;
  // Rows that would hit external APIs once the operator confirms.
  // Equals totalReturned when forceRefetch is set (cache wiped for
  // every matched row); otherwise just the cold-cache subset.
  totalPendingEnrichment: number;
  forceRefetch: boolean;
  expandedOutcodes: string[];
}

export interface LeadSearchPendingResponse {
  status: "pending";
  // `kind` flags whether the BE took the synchronous Lambda path
  // (`sync`, default for searches up to ~2,500 matched rows) or
  // kicked off a Step Functions chunked search (`chunked`, for
  // larger searches that take 30+ min). The FE renders different
  // copy + a "Search Status" link for chunked.
  kind?: "sync" | "chunked";
  operationKey: string;
  siteId: string;
  totalMatched: number;
  totalReturned: number;
  truncated: boolean;
  totalPendingEnrichment: number;
  expandedOutcodes: string[];
}

const postLeadSearch = async (
  authToken: string,
  body: LeadSearchRequest
): Promise<LeadSearchResponse> => {
  const { data } = await axiosConfig.post("/leads/search", body, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export default postLeadSearch;
